import { ROUTES } from '../../lib/routes';
import { navigate } from '../../router';
import { el } from '../../utils/dom';
import { showToast } from '../../components/toast';
import {
  createAdminInstallCase,
  deleteAdminInstallCase,
  listAdminInstallCases,
  patchAdminInstallCasePin,
  patchAdminInstallCasePublish,
  updateAdminInstallCase,
  type AdminFacilityType,
  type AdminInstallCase,
  type AdminInstallCaseInput,
} from '../../api/ev123InstallCasesAdmin';
import {
  installCaseFacilityBadgeClass,
  installCaseInstallTypeBadgeClass,
} from '../../data/installCaseBoard';
import { ensureAdminOrLeave } from './manageGuard';
import { buildForm, type FieldDef } from './manageForm';
import { buildImageSlots } from './manageImageSlots';

const FACILITY_OPTS: { value: AdminFacilityType; label: string }[] = [
  { value: 'apt', label: '공동주택' },
  { value: 'shop', label: '상업시설' },
  { value: 'office', label: '업무시설' },
  { value: 'public', label: '공공기관' },
  { value: 'etc', label: '기타' },
];

const FILTER_OPTS: { value: 'all' | AdminFacilityType; label: string }[] = [
  { value: 'all', label: '전체' },
  ...FACILITY_OPTS,
];

const FACILITY_LABEL: Record<string, string> = {
  apt: '공동주택',
  shop: '상업시설',
  office: '업무시설',
  public: '공공기관',
  etc: '기타',
};

const FIELDS: FieldDef[] = [
  { kind: 'text', key: 'title', label: '사례 제목', required: true, placeholder: '예: 공동주택 단지 · 지하주차장 증설' },
  { kind: 'select', key: 'facility_type', label: '시설 유형', options: FACILITY_OPTS },
  { kind: 'text', key: 'region', label: '지역', required: true, placeholder: '예: 수도권' },
  { kind: 'text', key: 'charger_count', label: '충전기 대수', required: true, placeholder: '예: 완속 4대' },
  { kind: 'text', key: 'install_type', label: '설치 방식', required: true, placeholder: '예: 위탁운영 / 자가설치' },
  { kind: 'textarea', key: 'summary', label: '요약', rows: 2, placeholder: '카드에 노출될 한 줄 요약' },
  {
    kind: 'textarea',
    key: 'description',
    label: '상세 설명',
    rows: 8,
    required: true,
    placeholder: '검토 배경, 결정 사항, 운영 포인트 등',
  },
  // 이미지 4종은 이미지 슬롯 UI(붙여넣기/파일 업로드/미리보기)에서 관리.
  // 폼 값은 hidden 처리된 텍스트 필드로 유지되어 저장 시점에 동기화된다.
  { kind: 'number', key: 'sort_order', label: '정렬 순서(작을수록 위)' },
  { kind: 'checkbox', key: 'is_published', label: '게시(공개)' },
  { kind: 'checkbox', key: 'is_pinned', label: '상단고정' },
];

function toInput(values: Record<string, string | number | boolean>): AdminInstallCaseInput {
  return {
    title: String(values.title || '').trim(),
    facility_type: String(values.facility_type || 'etc') as AdminFacilityType,
    region: String(values.region || '').trim(),
    charger_count: String(values.charger_count || '').trim(),
    install_type: String(values.install_type || '').trim(),
    summary: String(values.summary || ''),
    description: String(values.description || ''),
    main_image_url: String(values.main_image_url || ''),
    image_1_url: String(values.image_1_url || ''),
    image_2_url: String(values.image_2_url || ''),
    image_3_url: String(values.image_3_url || ''),
    is_published: values.is_published === true,
    is_pinned: values.is_pinned === true,
    sort_order: Number(values.sort_order) || 0,
  };
}

function fromRow(row: AdminInstallCase): Record<string, string | number | boolean> {
  return {
    title: row.title || '',
    facility_type: (row.facility_type || 'etc') as string,
    region: row.region || '',
    charger_count: row.charger_count || '',
    install_type: row.install_type || '',
    summary: row.summary || '',
    description: row.description || '',
    main_image_url: row.main_image_url || '',
    image_1_url: row.image_1_url || '',
    image_2_url: row.image_2_url || '',
    image_3_url: row.image_3_url || '',
    is_published: !!row.is_published,
    is_pinned: !!row.is_pinned,
    sort_order: row.sort_order ?? 0,
  };
}

export function renderManageInstallCasesPage(root: HTMLElement): void {
  void ensureAdminOrLeave();

  let rows: AdminInstallCase[] = [];
  let filterType: 'all' | AdminFacilityType = 'all';
  let searchQuery = '';
  let editingId: number | null = null;
  let busy = false;

  const wrap = el('div', { className: 'ev123-page-manage' });
  wrap.append(
    el('div', { className: 'ev123-page-head ev123-page-head--archive' }, [
      el('div', { className: 'ev123-container' }, [
        el('p', { className: 'ev123-portal-eyebrow', text: '관리자 모드' }),
        el('h1', { className: 'ev123-page-head__title', text: '설치사례 관리' }),
        el('p', {
          className: 'ev123-page-head__lead',
          text: '시설 유형별 설치사례를 등록·수정·삭제하고 게시 여부와 상단고정을 관리합니다.',
        }),
      ]),
    ]),
  );

  const container = el('div', { className: 'ev123-container ev123-stack' });
  container.appendChild(
    el('a', {
      className: 'ev123-link ev123-link--back',
      href: ROUTES.manage,
      'data-nav': '',
      text: '← 관리자 대시보드',
    }),
  );

  const toolbar = el('div', { className: 'ev123-manage-toolbar' });

  const ftSelect = el('select', {
    className: 'ev123-input ev123-manage-toolbar__select',
    'aria-label': '시설 유형 필터',
  }) as HTMLSelectElement;
  for (const o of FILTER_OPTS) {
    const opt = document.createElement('option');
    opt.value = o.value;
    opt.textContent = o.label;
    ftSelect.appendChild(opt);
  }
  ftSelect.addEventListener('change', () => {
    filterType = (ftSelect.value as 'all' | AdminFacilityType) || 'all';
    void reload();
  });

  const search = el('input', {
    className: 'ev123-input ev123-manage-toolbar__search',
    type: 'search',
    placeholder: '제목·지역 검색',
    'aria-label': '설치사례 관리 검색',
  }) as HTMLInputElement;
  let searchTimer: number | null = null;
  search.addEventListener('input', () => {
    if (searchTimer) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      searchQuery = search.value.trim();
      void reload();
    }, 250);
  });

  const reloadBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm',
    text: '새로고침',
  }) as HTMLButtonElement;
  reloadBtn.addEventListener('click', () => void reload());

  const newBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--primary ev123-btn--sm',
    text: '+ 신규 등록',
  }) as HTMLButtonElement;
  newBtn.addEventListener('click', () => openEditor(null));

  toolbar.append(ftSelect, search, reloadBtn, newBtn);

  const editorPanel = el('div', { className: 'ev123-card ev123-manage-editor', style: 'display:none' });
  const editorTitle = el('h2', { className: 'ev123-card__title', text: '새 설치사례 등록' });
  const form = buildForm(FIELDS);
  const imageSlots = buildImageSlots();
  const editorActions = el('div', { className: 'ev123-manage-editor__actions' });
  const cancelBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm',
    text: '취소',
  }) as HTMLButtonElement;
  cancelBtn.addEventListener('click', () => closeEditor());
  const saveBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--primary ev123-btn--sm',
    text: '저장',
  }) as HTMLButtonElement;
  saveBtn.addEventListener('click', () => void save());
  editorActions.append(cancelBtn, saveBtn);
  editorPanel.append(editorTitle, form.element, imageSlots.element, editorActions);

  const listCard = el('div', { className: 'ev123-card ev123-card--resource-list' });
  const listBody = el('div', { className: 'ev123-manage-list' });
  listCard.appendChild(listBody);

  container.append(toolbar, editorPanel, listCard);
  wrap.appendChild(container);
  root.appendChild(wrap);

  void reload();

  function openEditor(row: AdminInstallCase | null): void {
    editingId = row ? row.id : null;
    editorTitle.textContent = row ? `사례 수정 #${row.id}` : '새 설치사례 등록';
    if (row) {
      form.setValues(fromRow(row));
      imageSlots.setUrls({
        main_image_url: row.main_image_url || '',
        image_1_url: row.image_1_url || '',
        image_2_url: row.image_2_url || '',
        image_3_url: row.image_3_url || '',
      });
    } else {
      form.reset();
      imageSlots.reset();
    }
    editorPanel.style.display = '';
    editorPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function closeEditor(): void {
    editingId = null;
    editorPanel.style.display = 'none';
    form.reset();
    imageSlots.reset();
  }

  async function save(): Promise<void> {
    if (busy) return;
    const baseValues = form.values();
    const urls = imageSlots.getUrls();
    const merged: Record<string, string | number | boolean> = { ...baseValues, ...urls };
    const payload = toInput(merged);
    if (!payload.title) return showToast('제목을 입력해 주세요.');
    if (!payload.region) return showToast('지역을 입력해 주세요.');
    if (!payload.charger_count) return showToast('충전기 대수를 입력해 주세요.');
    if (!payload.install_type) return showToast('설치 방식을 입력해 주세요.');
    if (!payload.description) return showToast('상세 설명을 입력해 주세요.');
    busy = true;
    saveBtn.disabled = true;
    saveBtn.textContent = '저장 중…';
    try {
      const r = editingId
        ? await updateAdminInstallCase(editingId, payload)
        : await createAdminInstallCase(payload);
      if (!r.ok) {
        showToast(r.message || '저장에 실패했습니다.');
        if (r.status === 401 || r.status === 403) {
          navigate(ROUTES.homePublic);
          return;
        }
      } else {
        showToast(editingId ? '수정되었습니다.' : '등록되었습니다.');
        closeEditor();
        await reload();
      }
    } finally {
      busy = false;
      saveBtn.disabled = false;
      saveBtn.textContent = '저장';
    }
  }

  async function reload(): Promise<void> {
    listBody.replaceChildren(
      el('p', { className: 'ev123-card__desc', style: 'margin:1rem 0', text: '불러오는 중…' }),
    );
    const r = await listAdminInstallCases({ facility_type: filterType, q: searchQuery });
    if (!r.ok) {
      listBody.replaceChildren(
        el('p', {
          className: 'ev123-card__desc',
          style: 'margin:1rem 0',
          text: r.message || '목록을 불러오지 못했습니다.',
        }),
      );
      if (r.status === 401 || r.status === 403) navigate(ROUTES.homePublic);
      return;
    }
    rows = r.data;
    renderRows();
  }

  function renderRows(): void {
    listBody.replaceChildren();
    if (rows.length === 0) {
      listBody.appendChild(
        el('p', {
          className: 'ev123-card__desc',
          style: 'margin:1rem 0',
          text: '등록된 설치사례가 없습니다. “신규 등록”으로 추가해 주세요.',
        }),
      );
      return;
    }
    for (const row of rows) listBody.appendChild(renderRow(row));
  }

  function renderRow(row: AdminInstallCase): HTMLElement {
    const item = el('div', { className: 'ev123-manage-row' });
    const head = el('div', { className: 'ev123-manage-row__head' });

    const badges = el('div', { className: 'ev123-manage-row__badges' });
    badges.appendChild(
      el('span', {
        className: installCaseFacilityBadgeClass(row.facility_type),
        text: FACILITY_LABEL[row.facility_type] ?? '기타',
      }),
    );
    if (row.install_type) {
      badges.appendChild(
        el('span', {
          className: installCaseInstallTypeBadgeClass(row.install_type),
          text: row.install_type,
        }),
      );
    }
    if (row.is_pinned)
      badges.appendChild(el('span', { className: 'ev123-badge ev123-badge--pinned', text: '상단고정' }));
    badges.appendChild(
      el('span', {
        className: row.is_published
          ? 'ev123-badge ev123-badge--published'
          : 'ev123-badge ev123-badge--draft',
        text: row.is_published ? '게시중' : '비게시',
      }),
    );

    const titleBox = el('div', { className: 'ev123-manage-row__title-box' });
    titleBox.append(
      el('p', { className: 'ev123-manage-row__title', text: row.title || '(제목 없음)' }),
      el('p', {
        className: 'ev123-manage-row__meta',
        text: `#${row.id} · ${row.region || '지역 미정'} · ${row.charger_count || ''} · 정렬 ${row.sort_order ?? 0}${row.updated_at ? ` · 수정 ${row.updated_at.slice(0, 10)}` : ''}`,
      }),
    );
    if (row.summary) {
      titleBox.appendChild(el('p', { className: 'ev123-manage-row__summary', text: row.summary }));
    }

    head.append(badges, titleBox);

    const actions = el('div', { className: 'ev123-manage-row__actions' });

    const togglePub = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm',
      text: row.is_published ? '비게시 전환' : '게시 전환',
    }) as HTMLButtonElement;
    togglePub.addEventListener('click', () => void onTogglePublish(row, togglePub));

    const togglePin = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--header-subtle ev123-btn--sm',
      text: row.is_pinned ? '고정 해제' : '상단 고정',
    }) as HTMLButtonElement;
    togglePin.addEventListener('click', () => void onTogglePin(row, togglePin));

    const editBtn = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--secondary ev123-btn--sm',
      text: '수정',
    }) as HTMLButtonElement;
    editBtn.addEventListener('click', () => openEditor(row));

    const delBtn = el('button', {
      type: 'button',
      className: 'ev123-btn ev123-btn--danger ev123-btn--sm',
      text: '삭제',
    }) as HTMLButtonElement;
    delBtn.addEventListener('click', () => void onDelete(row, delBtn));

    actions.append(togglePub, togglePin, editBtn, delBtn);

    item.append(head, actions);
    return item;
  }

  async function onTogglePublish(row: AdminInstallCase, btn: HTMLButtonElement): Promise<void> {
    btn.disabled = true;
    const r = await patchAdminInstallCasePublish(row.id, !row.is_published);
    btn.disabled = false;
    if (!r.ok) {
      showToast(r.message || '상태 변경에 실패했습니다.');
      if (r.status === 401 || r.status === 403) navigate(ROUTES.homePublic);
      return;
    }
    showToast(r.data.is_published ? '게시되었습니다.' : '비게시 처리했습니다.');
    await reload();
  }

  async function onTogglePin(row: AdminInstallCase, btn: HTMLButtonElement): Promise<void> {
    btn.disabled = true;
    const r = await patchAdminInstallCasePin(row.id, !row.is_pinned);
    btn.disabled = false;
    if (!r.ok) {
      showToast(r.message || '상태 변경에 실패했습니다.');
      if (r.status === 401 || r.status === 403) navigate(ROUTES.homePublic);
      return;
    }
    showToast(r.data.is_pinned ? '상단고정 처리했습니다.' : '상단고정을 해제했습니다.');
    await reload();
  }

  async function onDelete(row: AdminInstallCase, btn: HTMLButtonElement): Promise<void> {
    const ok = window.confirm(`정말 삭제할까요?\n#${row.id} ${row.title || ''}`);
    if (!ok) return;
    btn.disabled = true;
    const r = await deleteAdminInstallCase(row.id);
    btn.disabled = false;
    if (!r.ok) {
      showToast(r.message || '삭제에 실패했습니다.');
      if (r.status === 401 || r.status === 403) navigate(ROUTES.homePublic);
      return;
    }
    showToast('삭제되었습니다.');
    await reload();
  }
}
